import React, { useState } from 'react';
import { Filter, Plus, X } from 'lucide-react';

// Same operator vocabulary as SharingRule/SharingRuleService and the backend
// src/utils/queryConditions.js — one meaning of "contains"/"gte"/etc across
// list filters and sharing-rule criteria, instead of two separate ones.
const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In (comma-separated)' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater Or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less Or Equal' },
];

const BLANK_CONDITION = { field: '', operator: 'equals', value: '' };

/**
 * Multi-field advanced filter builder, shared across the Lead/Account/Contact
 * list pages. Produces an array of { field, operator, value } conditions
 * (AND-combined) that the caller JSON.stringifies into the `advancedFilters`
 * query param — the same shape src/utils/queryConditions.js expects, and the
 * same vocabulary SharingRule already uses for its criteria.
 *
 * `fields` is an array of { value, label } describing which columns this
 * resource can be filtered on (differs per page — Leads/Accounts/Contacts
 * don't share a schema).
 */
export default function FilterBuilder({ fields, conditions, onChange }) {
  const [open, setOpen] = useState(false);
  const active = conditions.filter(c => c.field && c.value !== '').length;

  const updateCondition = (idx, patch) => {
    onChange(conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };
  const addCondition = () => onChange([...conditions, { ...BLANK_CONDITION }]);
  const removeCondition = (idx) => onChange(conditions.filter((_, i) => i !== idx));
  const clearAll = () => onChange([]);

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className={`h-9 px-3 rounded border text-[13px] font-semibold flex items-center gap-2 whitespace-nowrap shrink-0 transition-all ${
          active > 0 ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white border-[#D8DDE6] text-[#706E6B] hover:bg-[#F3F2F2]'
        }`}
      >
        <Filter size={14} /> Advanced Filters {active > 0 && `(${active})`}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[min(480px,calc(100vw-2rem))] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] z-50 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-[12px] font-bold text-[#080707] uppercase tracking-wider">Filter Conditions</h4>
              <button onClick={() => setOpen(false)} className="text-[#706E6B] hover:text-[#080707]"><X size={16} /></button>
            </div>

            {conditions.length === 0 && (
              <p className="text-[12px] text-[#706E6B]">No conditions yet — add one below.</p>
            )}

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {conditions.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <select
                    className="flex-1 h-9 px-2 border border-[#D8DDE6] rounded text-[12px]"
                    value={c.field}
                    onChange={e => updateCondition(idx, { field: e.target.value })}
                  >
                    <option value="">Field…</option>
                    {fields.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                  </select>
                  <select
                    className="w-36 h-9 px-2 border border-[#D8DDE6] rounded text-[12px]"
                    value={c.operator}
                    onChange={e => updateCondition(idx, { operator: e.target.value })}
                  >
                    {OPERATORS.map(op => <option key={op.value} value={op.value}>{op.label}</option>)}
                  </select>
                  <input
                    className="flex-1 h-9 px-2 border border-[#D8DDE6] rounded text-[12px]"
                    placeholder="Value…"
                    value={c.value}
                    onChange={e => updateCondition(idx, { value: e.target.value })}
                  />
                  <button onClick={() => removeCondition(idx)} className="text-[#706E6B] hover:text-[#BA0517] shrink-0">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-[#D8DDE6]">
              <button onClick={addCondition} className="text-[12px] font-bold text-[#0176D3] hover:underline flex items-center gap-1">
                <Plus size={14} /> Add Condition
              </button>
              {conditions.length > 0 && (
                <button onClick={clearAll} className="text-[12px] font-bold text-[#706E6B] hover:text-[#BA0517]">
                  Clear All
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
